import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowUpCircle,
  Bot,
  Check,
  ChevronDown,
  Copy,
  Terminal,
  Info,
  Laptop,
  Monitor,
  Pause,
  Play,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { api, jsonBody } from "../api";
import type { Locale } from "../i18n";
import { StatusBadge } from "./StatusBadge";
import { ModelControl } from "./ModelControl";
import { AgentInstall } from "./AgentInstall";
import { AgentIsolationSettings } from './AgentIsolationSettings';
import type { IsolationSettings } from './AgentIsolationSettings';
import { AgentRemoteAccess } from './AgentRemoteAccess';
import type { RemoteAccess } from './AgentRemoteAccess';
import { candidateKeys, parseRouteKey, providerLabel } from "./modelPlans";

type Metric = {
  successRate: number | null;
  total: number;
  success: number;
  failed: number;
  averageDurationMs: number | null;
  minimumDurationMs?: number | null;
  maximumDurationMs?: number | null;
};
type History = {
  from: string;
  until: string;
  state: string;
  availability: number | null;
  observedMinutes: number;
  expectedMinutes: number;
  observedUntil: string;
  partial: boolean;
  metrics: Metric;
};
type SystemMetrics = {
  cpuPercent: number | null;
  memoryTotalBytes: number | null;
  memoryFreeBytes: number | null;
  memoryUsedPercent: number | null;
  processRssBytes: number | null;
  load1: number | null;
  uptimeSeconds: number | null;
  sampledAt: string | null;
};
export type Agent = {
  id: string;
  enrollmentId: string | null;
  deviceId: string | null;
  label: string;
  platform: string | null;
  version: string;
  connectionIp?: string | null;
  update?: { targetVersion: string; phase: string; percent: number; error?: string };
  state: string;
  enrollmentState: string;
  mode: string;
  maxConcurrency: number;
  effectiveConcurrency?: number;
  reportedConcurrency?: number | null;
  schedulingHealth?: { consecutiveFailures: number; cooldownUntil: string | null; cooldownDurationMs?: number };
  recoveryProbe?: { state: string; attempts: number; nextAt?: string | null; code?: string } | null;
  activeTasks: number;
  diagnosticsEnabled: boolean;
  lastSeenAt: string | null;
  createdAt: string;
  approvedAt: string | null;
  capabilities: { localIpAddresses?: string[]; macAddresses?: string[]; paused?: boolean; pauseSource?: string; model?: string; provider?: string; diagnostics?: boolean; maxConcurrentCaptchaSessions?: number; selfUpdate?: boolean; monitorRefresh?: boolean; runtimeProbe?: boolean; system?: SystemMetrics | null; sshManagement?: boolean; remoteRestart?: boolean; remoteAccess?: RemoteAccess | null; tray?: { state: string; message?: string } | null };
  metrics: Record<string, Metric>;
  presence: {
    connectedAt: string | null;
    continuousOnlineSeconds: number;
    lastConnectionSeconds: number | null;
    onlineSeconds24h: number;
    availability24h: number | null;
    coveragePercent: number;
    observedMinutes: number;
    lastDisconnectAt: string | null;
    lastDisconnectReason: string | null;
    history: History[];
  };
};
type Fleet = { generatedAt: string; latestAgentVersion?: string; automaticAgentUpdates?: boolean; isolationSettings?: IsolationSettings; interval: string; enrollmentMode: string; agents: Agent[] };

function SchedulingNotice({ agent, locale, generatedAt }: { agent: Agent; locale: Locale; generatedAt?: string }) {
  const until = agent.schedulingHealth?.cooldownUntil;
  if (!until) return null;
  const remainingMs = Math.max(0, Date.parse(until) - Date.parse(generatedAt || new Date().toISOString()));
  const cooling = remainingMs > 0;
  const seconds = Math.ceil(remainingMs / 1000);
  const remaining = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  return <div className="agent-scheduling-status" role="status">
    <strong>{cooling
      ? (locale === "en" ? "Auto-isolated · new tasks paused" : "自动隔离 · 暂停接收新任务")
      : (locale === "en" ? "Recovery · independent test traffic" : "恢复探测 · 独立测试流量")}</strong>
    <span>{locale === "en" ? "No customer orders or production credits used" : "不使用正式订单，不扣正式额度"}</span>
    {agent.capabilities.runtimeProbe !== true && <small>{locale === "en" ? "Waiting for Agent upgrade to enable independent probes" : "等待 Agent 升级以执行独立测试"}</small>}
    {agent.recoveryProbe && <small>{locale === "en" ? `Probe ${agent.recoveryProbe.attempts} · ${agent.recoveryProbe.state}` : `测试 ${agent.recoveryProbe.attempts} 次 · ${agent.recoveryProbe.state === 'running' ? '正在自检' : agent.recoveryProbe.state === 'failed' ? '失败后自动重试' : '等待自动重试'}`}</small>}
    {cooling && <>
      <span>{locale === "en" ? `Cooldown remaining ${remaining}` : `冷却剩余 ${remaining}`}</span>
      <progress aria-label={locale === "en" ? "Cooldown time remaining" : "冷却剩余时间"} aria-valuetext={remaining} value={remainingMs} max={agent.schedulingHealth?.cooldownDurationMs || remainingMs} />
      <small>{locale === "en" ? `Next probe after ${new Date(until).toLocaleTimeString()}` : `下次探测 ${new Date(until).toLocaleTimeString()}`}</small>
    </>}
  </div>;
}
function needsAgentUpdate(current: string, latest?: string) {
  if (!latest || !/^\d+\.\d+\.\d+$/.test(current)) return false;
  const left = current.split('.').map(Number), right = latest.split('.').map(Number);
  for (let i = 0; i < 3; i++) if (left[i] !== right[i]) return left[i] < right[i];
  return false;
}
type ModelAssignment = {
  configId: string | null;
  source: "assigned" | "default";
  model: string | null;
  fallbackModel?: string | null;
  selectionMode?: "fixed" | "auto";
  name: string | null;
  version: number | null;
};
type ModelProviderSummary = { id: string; name: string };
type ModelSnapshot = {
  configs: Array<{
    id: string;
    name: string;
    providerId: string;
	    model: string;
	    fallbackModel: string;
    comparisonModels: string[];
	    rules?: Record<string, unknown>;
	    status: string;
  }>;
  providers: ModelProviderSummary[];
  defaultConfigId: string | null;
  assignments: Record<string, ModelAssignment>;
};

function modelPlanLabel(config: ModelSnapshot["configs"][number], locale: Locale) {
  const automatic = config.rules?.selectionMode === "auto";
  return `${config.name} · ${automatic ? (locale === "en" ? `Auto · ${candidateKeys(config).length} models` : `自动 · ${candidateKeys(config).length} 个模型`) : (locale === "en" ? "Fixed" : "固定")}`;
}

function modelPlanCandidates(config: ModelSnapshot["configs"][number], providers: ModelProviderSummary[]) {
  const keys = candidateKeys(config);
  return (config.rules?.selectionMode === "auto" ? keys : keys.slice(0, 1)).map(value => {
    const parsed = parseRouteKey(value, config.providerId);
    return {
      key: value,
      model: parsed.model,
      provider: providerLabel(parsed.providerId, providers),
    };
  });
}

const words = {
  en: {
    title: "Agent devices",
    search: "Search agents",
    all: "All states",
    online: "Online",
    offline: "Offline",
    unavailable: "Unavailable",
    pending: "Pending",
    revoked: "Revoked",
    draining: "Paused",
    legacy: "Legacy",
    agent: "Agent",
    state: "State",
    uptime: "Connection & execution history",
    success: "24h success",
    load: "Load",
    model: "Runtime preset",
    heartbeat: "Heartbeat",
    actions: "Actions",
    current: "This session",
    previous: "Last session",
    availability: "Observed uptime",
    coverage: "Coverage",
    online24: "Online in 24h",
    attempts: "attempts",
    approval: "Require approval",
    auto: "Auto-approve devices",
    policy: "Enrollment mode",
    refresh: "Refresh agents",
    downloads: "Install Agent",
    updateAgent: "Update Agent",
    updatingAgent: "Updating Agent",
    updateUnsupported: "Remote update needs Agent 1.3.6",
    dismissUpdate: "Dismiss update result",
    details: "Agent details",
    approve: "Approve",
    remove: "Delete",
    drain: "Pause new tasks",
    resume: "Resume agent",
    close: "Close details",
    copy: "Copy device ID",
    copied: "Copied",
    none: "No matching agents",
    error: "Unable to refresh agents.",
    updated: "Updated",
    sort: "Sort agents",
    priority: "Needs attention first",
    name: "Name",
    worst: "Lowest success first",
    busy: "Highest load first",
    unknown: "Unknown",
    partial: "Partial",
    fullId: "Device identity",
    version: "Version",
    mode: "Execution mode",
    diagnostics: "Remote diagnostics",
    diagnosticsOn: "Diagnostics on",
    diagnosticsOff: "Diagnostics off",
    connected: "Connected since",
    lastSeen: "Last heartbeat",
    registered: "Registered",
    approved: "Approved",
    disconnect: "Last disconnect",
    reason: "Disconnect reason",
    performance: "Attempt performance",
    duration: "Average success duration",
    window: "Window",
    successes: "Success / Attempts",
    confirmDelete:
      "Delete this device? Its identity cannot reconnect; history is retained.",
    observation: "Known samples",
    timeRange: "Observation period",
    sampling: "Observed uptime excludes unknown periods.",
    failures: "Failures",
    executionSuccess: "Execution success",
    average: "Average",
    fastest: "Fastest",
    slowest: "Slowest",
    platform: "Platform",
    free: "free slots",
    perCell: "Per cell",
    noAttempts: "No completed attempts",
    inProgress: "Interval in progress",
    timingNote: "Durations include successful attempts only.",
    historyNote: "32 intervals · local time · uptime samples retained for 8 days",
    loadingHistory: "Loading interval data…",
    machine: "Machine health",
    cpu: "CPU",
    memory: "Memory",
    process: "Agent process",
    load1: "Load 1m",
    sampled: "Sampled",
  },
  "zh-CN": {
    title: "Agent 设备",
    search: "搜索 Agent",
    all: "全部状态",
    online: "在线",
    offline: "离线",
    unavailable: "不可用",
    pending: "待批准",
    revoked: "已撤销",
    draining: "暂停接单",
    legacy: "旧版",
    agent: "Agent",
    state: "状态",
    uptime: "连接时长与执行历史",
    success: "24h 成功率",
    load: "负载",
    model: "运行方案",
    heartbeat: "心跳",
    actions: "操作",
    current: "本次在线",
    previous: "上次在线",
    availability: "已观测在线率",
    coverage: "覆盖率",
    online24: "24h 累计在线",
    attempts: "次尝试",
    approval: "需要管理员批准",
    auto: "自动批准设备",
    policy: "接入模式",
    refresh: "刷新 Agent",
    downloads: "安装 Agent",
    updateAgent: "升级 Agent",
    updatingAgent: "正在升级 Agent",
    updateUnsupported: "远程升级需要 Agent 1.3.6",
    dismissUpdate: "关闭升级结果",
    details: "Agent 详情",
    approve: "批准",
    remove: "删除",
    drain: "暂停接单",
    resume: "恢复接单",
    close: "关闭详情",
    copy: "复制设备 ID",
    copied: "已复制",
    none: "没有匹配的 Agent",
    error: "无法刷新 Agent 列表。",
    updated: "更新时间",
    sort: "Agent 排序",
    priority: "优先关注异常",
    name: "按名称",
    worst: "成功率最低优先",
    busy: "负载最高优先",
    unknown: "未知",
    partial: "部分覆盖",
    fullId: "设备身份",
    version: "版本",
    mode: "执行模式",
    diagnostics: "远程诊断",
    diagnosticsOn: "开启诊断",
    diagnosticsOff: "关闭诊断",
    connected: "本次连接开始",
    lastSeen: "最近心跳",
    registered: "注册时间",
    approved: "批准时间",
    disconnect: "最近断开",
    reason: "断开原因",
    performance: "尝试表现",
    duration: "成功平均耗时",
    window: "窗口",
    successes: "成功 / 尝试",
    confirmDelete: "删除此设备？原身份不能再接入，历史记录会保留。",
    observation: "已知采样",
    timeRange: "观测时段",
    sampling: "已观测在线率不包含未知时段。",
    failures: "失败",
    executionSuccess: "执行成功率",
    average: "平均",
    fastest: "最快",
    slowest: "最慢",
    platform: "平台",
    free: "可用名额",
    perCell: "每格时长",
    noAttempts: "无已结束尝试",
    inProgress: "本格统计中",
    timingNote: "耗时仅统计成功尝试。",
    historyNote: "32 个时段 · 本地时间 · 在线采样保留 8 天",
    loadingHistory: "正在加载时段数据…",
    machine: "机器健康",
    cpu: "CPU",
    memory: "内存",
    process: "Agent 进程",
    load1: "1 分钟负载",
    sampled: "采样时间",
  },
};
const duration = (value: number | null) =>
  value == null
    ? "—"
    : value < 60
      ? `${Math.floor(value)}s`
      : value < 3600
        ? `${Math.floor(value / 60)}m ${Math.floor(value % 60)}s`
        : value < 86400
          ? `${Math.floor(value / 3600)}h ${Math.floor((value % 3600) / 60)}m`
          : `${Math.floor(value / 86400)}d ${Math.floor((value % 86400) / 3600)}h`;
const rate = (value: number | null | undefined) =>
  value == null ? "—" : `${value.toFixed(1)}%`;
const rateClass = (value: number | null | undefined) =>
  value == null
    ? ""
    : value >= 95
      ? "agent-good"
      : value >= 75
      ? "agent-warning"
      : "agent-bad";
const latency = (value: number | null | undefined) =>
  value == null
    ? "—"
    : value < 1000
      ? `${Math.round(value)}ms`
      : `${(value / 1000).toFixed(1)}s`;
const percent = (value: number | null | undefined) =>
  value == null ? "—" : `${value.toFixed(1)}%`;
const bytes = (value: number | null | undefined) => {
  if (value == null || !Number.isFinite(value)) return "—";
  if (value >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(1)} GB`;
  if (value >= 1024 ** 2) return `${(value / 1024 ** 2).toFixed(0)} MB`;
  return `${(value / 1024).toFixed(0)} KB`;
};

function evidencePosition(anchor: HTMLElement) {
  const rect = anchor.getBoundingClientRect();
  const inset = Math.min(340, window.innerWidth - 20) / 2 + 10;
  const above = rect.top >= 350;
  const below = !above && window.innerHeight - rect.bottom >= 350;
  return {
    left: Math.max(inset, Math.min(window.innerWidth - inset, rect.left + rect.width / 2)),
    top: above ? rect.top - 8 : below ? rect.bottom + 8 : 10,
    below: !above,
    maxHeight: above ? rect.top - 18 : below ? window.innerHeight - rect.bottom - 18 : window.innerHeight - 20,
  };
}

export function AgentFleet({ locale }: { locale: Locale }) {
  const t = words[locale];
  const [fleet, setFleet] = useState<Fleet | null>(null),
    [notice, setNotice] = useState(""),
    [query, setQuery] = useState(""),
    [filter, setFilter] = useState("all"),
    [sort, setSort] = useState("priority"),
    [selectedId, setSelectedId] = useState<string | null>(null),
    [pending, setPending] = useState(false),
    [downloads, setDownloads] = useState(false),
    [copied, setCopied] = useState(false),
    [updatingAgentId, setUpdatingAgentId] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<{
    point: History;
    agentLabel: string;
    id: string;
    left: number;
    top: number;
    below: boolean;
    maxHeight: number;
  } | null>(null);
  const [metricWindow, setMetricWindow] = useState("15m");
  const [modelSnapshot, setModelSnapshot] = useState<ModelSnapshot>({
    configs: [],
    providers: [],
    defaultConfigId: null,
    assignments: {},
    });
  const [actionNotice, setActionNotice] = useState("");
  const request = useRef<AbortController | null>(null);
  const writing = useRef(false);
  const downloadMenu = useRef<HTMLDivElement | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const openDetails = (id: string) => {
    opener.current = document.activeElement as HTMLElement;
    setSelectedId(id);
    setCopied(false);
    setEvidence(null);
    setActionNotice("");
  };
  const reload = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    try {
      const snapshot = await api<Fleet>(`/api/admin/agents?interval=${metricWindow}`, {
        signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        setFleet({ ...snapshot, interval: metricWindow });
        setEvidence(current => {
          if (!current) return null;
          for (const agent of snapshot.agents) {
            const point = agent.presence.history.find(point => `history-${agent.id}-${point.from}` === current.id);
            if (point) return { ...current, point, agentLabel: agent.label };
          }
          return null;
        });
        setNotice("");
      }
    } catch {
      if (!controller.signal.aborted) setNotice(t.error);
    } finally {
      if (request.current === controller) request.current = null;
    }
  }, [t.error, metricWindow]);
  useEffect(() => {
    const dismiss = () => setEvidence(null);
    const scroll = () => setEvidence(current => {
      const anchor = document.activeElement as HTMLElement | null;
      if (!current || anchor?.id !== current.id) return null;
      const rect = anchor.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return null;
      return { ...current, ...evidencePosition(anchor) };
    });
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") dismiss(); };
    window.addEventListener("resize", dismiss);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("keydown", escape);
    return () => {
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("keydown", escape);
    };
  }, []);
  const reloadModels = useCallback(async () => {
    try {
      const result = await api<ModelSnapshot>("/api/admin/model-configs");
      setModelSnapshot({
        configs: result.configs || [],
        providers: result.providers || [],
        defaultConfigId: result.defaultConfigId || null,
        assignments: result.assignments || {},
      });
    } catch {
      /* model controls remain usable with the last snapshot */
    }
  }, []);
  useEffect(() => {
    let alive = true;
    const refresh = () => {
      if (alive && !writing.current && !request.current) {
        void reload();
        void reloadModels();
      }
    };
    queueMicrotask(refresh);
    const timer = setInterval(refresh, 2_000);
    return () => {
      alive = false;
      clearInterval(timer);
      request.current?.abort();
      request.current = null;
    };
  }, [reload, reloadModels]);
  useEffect(() => {
    if (!downloads) return;
    const click = (event: PointerEvent) => {
      if (!downloadMenu.current?.contains(event.target as Node))
        setDownloads(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDownloads(false);
        downloadMenu.current?.querySelector("button")?.focus();
      }
    };
    document.addEventListener("pointerdown", click);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", click);
      document.removeEventListener("keydown", escape);
    };
  }, [downloads]);
  const mutate = async (path: string, method: string, body?: unknown, successMessage = "") => {
    if (writing.current) return;
    writing.current = true;
    request.current?.abort();
    setPending(true);
    setActionNotice("");
    try {
      await api(path, { method, ...(body ? { body: jsonBody(body) } : {}) });
      await reload();
      if (successMessage) setActionNotice(successMessage);
    } catch (error) {
      setActionNotice(error instanceof Error ? error.message : t.error);
    } finally {
      writing.current = false;
      setPending(false);
    }
  };
  const deviceAction = (agent: Agent, action: "approve" | "delete") => {
    if (!agent.enrollmentId || (action !== "approve" && !window.confirm(t.confirmDelete))) return;
    void mutate(
      `/api/admin/devices/${agent.enrollmentId}${action === "delete" ? "" : `/${action}`}`,
      action === "delete" ? "DELETE" : "POST",
      action === "delete" ? undefined : {},
    );
  };
  const updateAgent = async (agent: Agent) => {
    if (updatingAgentId) return;
    if (!window.confirm(locale === "en"
      ? `Update ${agent.label}: v${agent.version} → v${fleet?.latestAgentVersion}? Download and verify, stop, replace program files, then restart. Agent will briefly go offline. Local data is preserved.`
      : `升级 ${agent.label}：v${agent.version} → v${fleet?.latestAgentVersion}？将下载并校验升级包、停止 Agent、替换程序并重新启动，期间会短暂离线，本地数据会保留。`)) return;
    setUpdatingAgentId(agent.id);
    setActionNotice("");
    try {
      const result = await api<{ targetVersion?: string; alreadyCurrent?: boolean }>(
        `/api/admin/agents/${agent.id}/update`,
        { method: "POST", body: jsonBody({}) },
      );
      setActionNotice(
        result.alreadyCurrent
          ? locale === "en"
            ? "Agent is already current."
            : "Agent 已是最新版本。"
          : "",
      );
      await reload();
    } catch (error) {
      setActionNotice(error instanceof Error ? error.message : t.error);
    } finally {
      setUpdatingAgentId(null);
    }
  };
  const agents = useMemo(() => {
    const priority: Record<string, number> = {
      pending: 0,
      offline: 1,
      draining: 2,
      online: 3,
      revoked: 4,
    };
    return (fleet?.agents || [])
      .filter(
        (agent) =>
          (filter === "all" || agent.state === filter) &&
          [
            agent.label,
            agent.deviceId,
            agent.platform,
            agent.capabilities.model,
            agent.capabilities.provider,
          ]
            .join(" ")
            .toLowerCase()
            .includes(query.toLowerCase().trim()),
      )
      .sort((left, right) =>
        sort === "name"
          ? left.label.localeCompare(right.label)
          : sort === "worst"
            ? (left.metrics[metricWindow]?.successRate ?? 101) -
              (right.metrics[metricWindow]?.successRate ?? 101)
            : sort === "busy"
              ? right.activeTasks / (right.effectiveConcurrency ?? right.maxConcurrency) -
                left.activeTasks / (left.effectiveConcurrency ?? left.maxConcurrency)
              : (priority[left.state] ?? 5) - (priority[right.state] ?? 5) ||
                left.label.localeCompare(right.label),
      );
  }, [fleet, filter, query, sort, metricWindow]);
  const selected = fleet?.agents.find((agent) => agent.id === selectedId);
  useEffect(() => {
    if (!selected?.id) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedId(null);
        return;
      }
      if (event.key !== "Tab") return;
      const controls = [
        ...document.querySelectorAll<HTMLElement>(
          ".agent-detail button:not(:disabled), .agent-detail select:not(:disabled), .agent-detail a[href]",
        ),
      ];
      const next = event.shiftKey ? controls.at(-1) : controls[0];
      if (
        document.activeElement ===
        (event.shiftKey ? controls[0] : controls.at(-1))
      ) {
        event.preventDefault();
        next?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handler);
      opener.current?.focus();
    };
  }, [selected?.id]);
  const timestamp = (value: string | null) =>
    value ? new Date(value).toLocaleString(locale, { hour12: false }) : "—";
  const age = (value: string | null) =>
    value && fleet
      ? duration(
          Math.max(
            0,
            (Date.parse(fleet.generatedAt) - Date.parse(value)) / 1000,
          ),
        )
      : "—";
  const counts = (fleet?.agents || []).reduce<Record<string, number>>(
    (result, agent) => {
      result[agent.state] = (result[agent.state] || 0) + 1;
      return result;
    },
    {},
  );
  const status = (value: string) =>
    value === "degraded"
      ? t.partial
      : t[
          value as
            | "online"
            | "offline"
            | "pending"
            | "revoked"
            | "draining"
            | "unknown"
        ] || value;
  const platform = (value: string | null) =>
    !value || value === "unknown"
      ? t.unknown
      : value === "macos"
        ? "macOS"
        : value === "windows"
          ? "Windows"
          : value === "linux"
            ? "Linux"
            : value;
  const reason = (value: string | null) =>
    !value
      ? "—"
      : {
          server_restart: locale === "en" ? "Server restarted" : "服务器重启",
          disconnected: locale === "en" ? "Connection closed" : "连接已断开",
          replaced: locale === "en" ? "Connection replaced" : "连接已替换",
        }[value] || value;
  const metricSummary = (metric: Metric | undefined) => {
    if (!metric) return "";
    return `${t.executionSuccess} ${rate(metric.successRate)} (${metric.success || 0}/${metric.total || 0}) · ${t.average} ${latency(metric.averageDurationMs)} · ${t.fastest} ${latency(metric.minimumDurationMs)} · ${t.slowest} ${latency(metric.maximumDurationMs)}`;
  };
  const changePolicy = (mode: string) => {
    if (
      mode === "auto" &&
      !window.confirm(
        locale === "en"
          ? "Allow new devices to join without administrator approval?"
          : "允许新设备未经管理员批准直接接入？",
      )
    )
      return;
    void mutate("/api/admin/devices/settings", "PATCH", { mode });
  };
  const applyModelAssignment = async (
    workerIds: string[],
    configId: string | null,
  ) => {
    if (!workerIds.length || writing.current) return;
    writing.current = true;
    setPending(true);
    setActionNotice("");
    try {
      const result = await api<{ delivered: string[]; failed: Array<{ workerId: string }> }>("/api/admin/model-configs/assignments", {
        method: "PATCH",
        body: jsonBody({ workerIds, configId }),
      });
      await reloadModels();
      setActionNotice(result.failed?.length
        ? (locale === "en" ? "Preset saved; some Agents have not acknowledged it. Offline Agents receive it on reconnect." : "方案已保存，部分 Agent 尚未确认；离线设备会在重连时同步。")
        : "");
    } catch (error) {
      setActionNotice(error instanceof Error ? error.message : t.error);
    } finally {
      writing.current = false;
      setPending(false);
    }
  };
  const historyLabel = (point: History) =>
    `${timestamp(point.from)} – ${timestamp(point.until)} · ${status(point.state)} · ${t.availability} ${rate(point.availability)} · ${t.observation} ${point.observedMinutes}/${point.expectedMinutes}m · ${metricSummary(point.metrics)}`;
  const cellClass = (point: History) => point.metrics?.total
    ? `execution-${point.metrics.successRate! >= 95 ? "good" : point.metrics.successRate! >= 75 ? "warning" : "bad"}`
    : point.state === "offline" ? "presence-offline" : point.state === "unknown" ? "presence-unknown" : "execution-idle";
  const showEvidence = (point: History, anchor: HTMLElement, agentLabel: string) => {
    setEvidence({
      point, agentLabel, id: anchor.id,
      ...evidencePosition(anchor),
    });
  };
  const selectedModelPlan = (agentId: string) => {
    const assignment = (modelSnapshot.assignments || {})[agentId];
    return modelSnapshot.configs.find(config => config.id === assignment?.configId)
      || modelSnapshot.configs.find(config => config.id === modelSnapshot.defaultConfigId);
  };
  const rateLabel =
    locale === "en" ? `Last ${metricWindow} success` : `最近 ${metricWindow} 成功率`;
  const historyReady = fleet?.interval === metricWindow;
  if (!fleet)
    return (
      <section className="agent-fleet" aria-label={t.title}>
        <h2>{t.title}</h2>
        <div className="ops-loading" role="status">
          {notice ||
            (locale === "en" ? "Loading agents..." : "正在加载 Agent...")}
        </div>
        {notice && (
          <button
            className="button button-outline"
            onClick={() => void reload()}
          >
            <RefreshCw size={16} />
            {t.refresh}
          </button>
        )}
      </section>
    );
  return (
    <section className="agent-fleet" aria-label={t.title}>
      <div className="agent-fleet-heading">
        <div>
          <h2>{t.title}</h2>
          <small>
            {fleet ? `${t.updated} ${timestamp(fleet.generatedAt)}` : t.error}
            {fleet?.latestAgentVersion && ` · ${locale === "en" ? "Latest Agent" : "最新 Agent"} v${fleet.latestAgentVersion}`}
          </small>
        </div>
        <div className="agent-heading-actions">
          <label className="agent-auto-update" title={locale === "en" ? "Push newer versions when an Agent is online and idle" : "仅向版本落后且在线的 Agent 推送，等待当前任务完成后升级"}>
            <span>{locale === "en" ? "Auto-push Agent updates" : "自动推送 Agent 更新"}</span>
            <input type="checkbox" checked={fleet?.automaticAgentUpdates || false} disabled={pending || !fleet} onChange={event => void mutate('/api/admin/agents/update-settings', 'PATCH', { automatic: event.target.checked })} />
          </label>
          <label>
            {t.policy}
            <select
              aria-label={t.policy}
              value={fleet?.enrollmentMode || "approval"}
              disabled={pending || !fleet}
              onChange={(event) => changePolicy(event.target.value)}
            >
              <option value="approval">{t.approval}</option>
              <option value="auto">{t.auto}</option>
            </select>
          </label>
          <div className="agent-download-menu" ref={downloadMenu}>
            <button
              className="button button-outline"
              onClick={() => setDownloads(!downloads)}
              aria-expanded={downloads}
              aria-controls="agent-downloads"
            >
              <Terminal size={16} />
              {t.downloads}
              <ChevronDown size={14} />
            </button>
            {downloads && (
              <div id="agent-downloads">
                <AgentInstall locale={locale} onClose={() => { setDownloads(false); downloadMenu.current?.querySelector('button')?.focus(); }} />
              </div>
            )}
          </div>
          <button
            className="icon-button"
            aria-label={t.refresh}
            title={t.refresh}
            disabled={pending}
            onClick={() => void reload()}
          >
            <RefreshCw size={17} />
          </button>
        </div>
      </div>
      {fleet?.isolationSettings && <AgentIsolationSettings
        key={`${fleet.isolationSettings.failureLimit}:${fleet.isolationSettings.failureWindowSeconds}`}
        settings={fleet.isolationSettings} locale={locale} pending={pending}
        onSave={settings => void mutate('/api/admin/agents/isolation-settings', 'PATCH', settings, locale === 'en' ? 'Isolation policy saved.' : '隔离策略已保存。')}
      />}
      {notice && (
        <div className="alert alert-error" role="status">
          {notice}
          {notice === t.error
            ? locale === "en"
              ? " Showing the last successful snapshot."
              : " 当前展示上一次成功获取的记录。"
            : ""}
        </div>
      )}
      {actionNotice && !selected && (
        <div className="alert alert-error" role="status">
          {actionNotice}
        </div>
      )}
      <ModelControl locale={locale} />
      <div className="agent-fleet-summary">
        {["online", "offline", "pending", "draining", "revoked"].map(
          (state) => (
            <span key={state}>
              <i className={`agent-dot agent-dot-${state}`} />
              {status(state)} <strong>{counts[state] || 0}</strong>
            </span>
          ),
        )}
      </div>
      <div className="agent-filters">
        <label className="agent-search">
          <Search size={16} />
          <input
            type="search"
            aria-label={t.search}
            placeholder={t.search}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <select
          aria-label={t.state}
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        >
          <option value="all">{t.all}</option>
          {["online", "offline", "pending", "draining", "revoked"].map(
            (state) => (
              <option key={state} value={state}>
                {status(state)}
              </option>
            ),
          )}
        </select>
        <select
          aria-label={t.sort}
          value={sort}
          onChange={(event) => setSort(event.target.value)}
        >
          {[
            ["priority", t.priority],
            ["name", t.name],
            ["worst", t.worst],
            ["busy", t.busy],
          ].map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <small>
          {agents.length} / {fleet?.agents.length || 0}
        </small>
      </div>
      <div className="agent-window-bar">
        <div className="agent-history-controls">
        <span>{t.perCell}</span>
        <div className="ops-ranges" role="group" aria-label={t.perCell}>
          {["1m", "15m", "30m", "1h", "24h"].map((window) => (
            <button
              key={window}
              aria-pressed={metricWindow === window}
              onClick={() => { setEvidence(null); setMetricWindow(window); }}
            >
              {window}
            </button>
          ))}
        </div>
        <small>{t.historyNote}</small>
        </div>
        <div className="agent-history-legend">
          <span><i className="agent-dot agent-dot-online" />≥95%</span>
          <span><i className="agent-dot agent-dot-draining" />75–95%</span>
          <span><i className="agent-dot agent-dot-failed" />&lt;75%</span>
          <span>
            <i className="agent-dot agent-dot-unavailable" />
            {t.offline}
          </span>
          <span>
            <i className="agent-dot agent-dot-idle" />
            {t.noAttempts}
          </span>
          <span>
            <i className="agent-dot agent-dot-unknown" />
            {t.unknown}
          </span>
        </div>
      </div>
      <div className="agent-table-wrap">
        <table className="agent-table">
          <thead>
            <tr>
              {[
                t.agent,
                t.state,
                t.uptime,
                rateLabel,
                t.load,
                t.model,
                t.heartbeat,
                t.actions,
              ].map((label) => (
                <th key={label}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agents.map((agent) => {
              const metric = agent.metrics[metricWindow],
                online = ["online", "draining"].includes(agent.state),
                system = agent.capabilities.system,
                effectiveConcurrency = agent.effectiveConcurrency ?? agent.maxConcurrency;
              return (
                <tr
                  key={agent.id}
                  className={selectedId === agent.id ? "selected" : ""}
                >
                  <td data-label={t.agent}>
                    <div className="agent-identity-cell">
                      <button
                        className="agent-identity"
                        title={agent.label}
                        onClick={() => openDetails(agent.id)}
                      >
                        <span className="agent-platform">
                          {agent.platform === "macos" ? (
                            <Laptop size={19} />
                          ) : agent.platform === "windows" ? (
                            <Monitor size={19} />
                          ) : (
                            <Bot size={19} />
                          )}
                        </span>
                        <span>
                          <strong>{agent.label}</strong>
                          <small>
                            {platform(agent.platform)} · v{agent.version || "—"}
                            {agent.enrollmentState === "legacy"
                              ? ` · ${t.legacy}`
                              : ""}
                          </small>
                          <span className="agent-network-info">
                            <small className="agent-connection-ip agent-lan-ip" title={locale === "en" ? "Local interface addresses; update older Agents to report LAN IP" : "Agent 本机网卡地址；旧版 Agent 需更新后才能上报局域网 IP"}>
                              {locale === "en" ? "LAN IP" : "局域网 IP"} · {Array.isArray(agent.capabilities.localIpAddresses) && agent.capabilities.localIpAddresses.length ? agent.capabilities.localIpAddresses.join(" / ") : (locale === "en" ? "Not reported" : "未上报")}
                            </small>
                            <small className="agent-connection-ip agent-public-ip" title={locale === "en" ? "Agent connection source observed by the server; not the task proxy exit IP" : "服务端看到的 Agent 连接来源 IP，不是任务代理的出口 IP"}>
                              {locale === "en" ? "Public IP" : "公网 IP"} · {agent.connectionIp || (locale === "en" ? "Not recorded" : "未记录")}
                            </small>
                            <small className="agent-connection-ip agent-mac" title={locale === "en" ? "MAC addresses reported by the Agent; multiple network adapters may be listed" : "Agent 上报的网卡 MAC 地址；多网卡机器可能显示多个地址"}>
                              MAC · {Array.isArray(agent.capabilities.macAddresses) && agent.capabilities.macAddresses.length ? agent.capabilities.macAddresses.join(" / ") : (locale === "en" ? "Not reported" : "未上报")}
                            </small>
                          </span>
                        </span>
                      </button>
                    </div>
                    <SchedulingNotice agent={agent} locale={locale} generatedAt={fleet?.generatedAt} />
                  </td>
                  <td data-label={t.state}>
                    <StatusBadge
                      value={agent.state}
                      label={agent.state === "draining" || agent.capabilities.paused ? `${locale === "en" ? (agent.activeTasks ? "Paused · finishing tasks" : "Paused") : (agent.activeTasks ? "暂停接单 · 等待当前任务完成" : "已暂停接单")}（${agent.capabilities.pauseSource === "admin" || !agent.capabilities.paused ? (locale === "en" ? "Console" : "管理端发起") : (locale === "en" ? "Local Agent" : "本地发起")}）` : status(agent.state)}
                    />
                  </td>
                  <td>
                    <div className="agent-presence">
                      <div>
                        <strong>
                          {duration(
                            online
                              ? agent.presence.continuousOnlineSeconds
                              : agent.presence.lastConnectionSeconds,
                          )}
                        </strong>
                        <small>{online ? t.current : t.previous}</small>
                      </div>
                      <div
                        className="agent-presence-cells"
                        aria-busy={!historyReady}
                        tabIndex={0}
                        role="group"
                        aria-label={`${t.availability} ${rate(agent.presence.availability24h)}, ${t.coverage} ${rate(agent.presence.coveragePercent)}`}
                        onFocus={(event) => {
                          if (event.target === event.currentTarget)
                            (
                              event.currentTarget
                                .lastElementChild as HTMLElement
                            )?.focus({ preventScroll: true });
                        }}
                        onKeyDown={(event) => {
                          if (!["ArrowLeft", "ArrowRight"].includes(event.key))
                            return;
                          event.preventDefault();
                          const cells = [
                            ...event.currentTarget.children,
                          ] as HTMLElement[];
                          const index = cells.indexOf(
                            document.activeElement as HTMLElement,
                          );
                          cells[
                            Math.max(
                              0,
                              Math.min(
                                cells.length - 1,
                                index + (event.key === "ArrowLeft" ? -1 : 1),
                              ),
                            )
                          ]?.focus({ preventScroll: true });
                        }}
                      >
                        {historyReady ? agent.presence.history.map((point) => (
                          <span
                            key={point.from}
                            id={`history-${agent.id}-${point.from}`}
                            tabIndex={-1}
                            className={`${cellClass(point)}${point.partial ? " interval-partial" : ""}`}
                            aria-label={historyLabel(point)}
                            aria-describedby={evidence?.id === `history-${agent.id}-${point.from}` ? "agent-history-tooltip" : undefined}
                            onMouseEnter={(event) =>
                              showEvidence(point, event.currentTarget, agent.label)
                            }
                            onMouseLeave={(event) => {
                              if (document.activeElement !== event.currentTarget) setEvidence(null);
                            }}
                            onFocus={(event) => {
                              event.currentTarget.scrollIntoView({ block: "nearest", behavior: "instant" });
                              showEvidence(point, event.currentTarget, agent.label);
                            }}
                            onClick={(event) => showEvidence(point, event.currentTarget, agent.label)}
                            onBlur={() => setEvidence(null)}
                          >
                            <i />
                          </span>
                        )) : <small className="agent-history-loading" role="status">{notice || t.loadingHistory}</small>}
                      </div>
                      {historyReady && <div className="agent-history-axis">
                        <span>{new Date(agent.presence.history[0]?.from).toLocaleString(locale, { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                        <span>{new Date(agent.presence.history.at(-1)?.until || "").toLocaleString(locale, { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                      </div>}
                      <small>
                        24h {t.availability}{" "}
                        <b
                          className={rateClass(agent.presence.availability24h)}
                        >
                          {rate(agent.presence.availability24h)}
                        </b>{" "}
                        · {t.coverage} {rate(agent.presence.coveragePercent)}
                      </small>
                    </div>
                  </td>
                  <td data-label={rateLabel}>
                    <div className="agent-stat">
                      <strong className={rateClass(metric?.successRate)}>
                        {rate(metric?.successRate)}
                      </strong>
                      <small>
                        {metric?.success || 0} / {metric?.total || 0}{" "}
                        {t.attempts}
                      </small>
                    </div>
                  </td>
                  <td data-label={t.load}>
                    <div className="agent-load">
                      <strong>
                        {agent.activeTasks}
                        <span> / {effectiveConcurrency}</span>
                      </strong>
                      <progress
                        value={Math.min(
                          agent.activeTasks,
                          effectiveConcurrency,
                        )}
                        max={effectiveConcurrency}
                        aria-label={`${t.load}: ${agent.activeTasks}/${effectiveConcurrency}`}
                      />
                      <small>{agent.mode}</small>
                      {system && (
                        <small className="agent-resource-line">
                          <span>CPU {percent(system.cpuPercent)}</span>
                          <span>RAM {percent(system.memoryUsedPercent)}</span>
                        </small>
                      )}
                    </div>
                  </td>
                  <td data-label={t.model}>
                    <div className="agent-model">
                      <select
                        aria-label={`${t.model}: ${agent.label}`}
                        title={selectedModelPlan(agent.id) ? modelPlanLabel(selectedModelPlan(agent.id)!, locale) : undefined}
                        value={
                          modelSnapshot.assignments[agent.id]?.source === "assigned" ? modelSnapshot.assignments[agent.id]?.configId || "" : ""
                        }
                        disabled={
                          pending ||
                          !modelSnapshot.configs.some(
                            (config) => config.status === "active",
                          )
                        }
                        onChange={(event) =>
                          void applyModelAssignment(
                            [agent.id],
                            event.target.value || null,
                          )
                        }
                      >
	                        <option value="">
	                          {locale === "en" ? "Follow default" : "跟随默认方案"}
	                          {modelSnapshot.configs.find(config => config.id === modelSnapshot.defaultConfigId)
	                            ? " - " + modelPlanLabel(modelSnapshot.configs.find(config => config.id === modelSnapshot.defaultConfigId)!, locale)
	                            : ""}
	                        </option>
                        {modelSnapshot.configs
                          .filter((config) => config.status === "active")
                          .map((config) => (
	                            <option key={config.id} value={config.id}>
	                              {modelPlanLabel(config, locale)}
	                            </option>
                          ))}
                      </select>
                      <small>
                        {(modelSnapshot.assignments || {})[agent.id]?.source ===
                        "assigned"
                          ? locale === "en"
                            ? "Manually assigned"
                            : "手动指定"
                          : locale === "en"
                            ? "Following default"
                            : "跟随默认"}
	                        {selectedModelPlan(agent.id) ? " · " + (selectedModelPlan(agent.id)!.rules?.selectionMode === "auto" ? (locale === "en" ? "Automatic model range" : "自动选择范围") : (locale === "en" ? "Fixed model" : "固定模型")) : ""}
	                      </small>
	                      <div className="agent-model-chips" aria-label={locale === "en" ? "Effective models" : "当前模型范围"}>
	                        {selectedModelPlan(agent.id)
	                          ? modelPlanCandidates(selectedModelPlan(agent.id)!, modelSnapshot.providers).map(candidate => (
	                            <span key={candidate.key}>
	                              {candidate.model}
	                              <small>{candidate.provider}</small>
	                            </span>
	                          ))
	                          : null}
	                      </div>
                    </div>
                  </td>
                  <td data-label={t.heartbeat}>
                    <span
                      title={timestamp(agent.lastSeenAt)}
                      className="agent-heartbeat"
                    >
                      {age(agent.lastSeenAt)}
                    </span>
                  </td>
                  <td>
                    <div className="agent-row-actions">
                      <button
                        className="icon-button"
                        aria-label={`${t.details}: ${agent.label}`}
                        title={t.details}
                        onClick={() => openDetails(agent.id)}
                      >
                        <Info size={16} />
                      </button>
                      {agent.state === "pending" && (
                        <button
                          className="icon-button agent-good"
                          aria-label={t.approve}
                          title={t.approve}
                          disabled={pending}
                          onClick={() => deviceAction(agent, "approve")}
                        >
                          <Check size={16} />
                        </button>
                      )}
                      {["online", "draining"].includes(agent.state) && needsAgentUpdate(agent.version, fleet?.latestAgentVersion) && (
                        <button
                          className="icon-button agent-good"
                          aria-label={`${t.updateAgent}: ${agent.label}`}
                          title={agent.capabilities.selfUpdate ? `${t.updateAgent}: v${agent.version} → v${fleet?.latestAgentVersion}` : t.updateUnsupported}
                          disabled={
                            updatingAgentId === agent.id ||
                            Boolean(agent.update && !["completed", "failed"].includes(agent.update.phase)) ||
                            agent.activeTasks > 0 ||
                            agent.capabilities.selfUpdate !== true
                          }
                          onClick={() => void updateAgent(agent)}
                        >
                          <ArrowUpCircle size={16} />
                        </button>
                      )}
                      {agent.update && (
                        <div className="agent-update-progress" role="status">
                          <span>{locale === "en"
                            ? ({ transferring: "Transferring", verifying: "Verifying", stopping: "Stopping", restarting: "Replacing / starting", completed: "Updated and online", failed: "Update failed" }[agent.update.phase] || agent.update.phase)
                            : ({ transferring: "传输升级包", verifying: "校验升级包", stopping: "停止 Agent", restarting: "替换程序 / 等待启动", completed: "升级成功 · 已启动", failed: "升级失败" }[agent.update.phase] || agent.update.phase)} · {agent.update.percent ?? 0}%</span>
                          {["completed", "failed"].includes(agent.update.phase) && (
                            <button type="button" className="agent-update-dismiss" aria-label={t.dismissUpdate} title={t.dismissUpdate} onClick={() => void mutate(`/api/admin/agents/${agent.id}/update`, "DELETE")}>×</button>
                          )}
                          <progress max={100} value={agent.update.percent ?? 0} aria-label={t.updatingAgent} />
                          {agent.update.error && <span>{agent.update.error}</span>}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!agents.length && (
              <tr>
                <td colSpan={8} className="empty-cell">
                  {t.none}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {evidence &&
        createPortal(
          <div
            role="tooltip"
            id="agent-history-tooltip"
            className="agent-history-tooltip"
            style={{
              left: evidence.left,
              top: evidence.top,
              maxHeight: evidence.maxHeight,
              transform: `translate(-50%,${evidence.below ? "0" : "-100%"})`,
            }}
          >
            <header><strong>{evidence.agentLabel}</strong><span>{metricWindow} / {t.perCell}</span></header>
            <div className="history-tooltip-time">{timestamp(evidence.point.from)}<br />→ {timestamp(evidence.point.until)}</div>
            {evidence.point.partial && <div className="history-tooltip-live">● {t.inProgress} · {t.updated} {new Date(evidence.point.observedUntil).toLocaleTimeString(locale)}</div>}
            <div className="history-tooltip-result">
              <div><small>{t.executionSuccess}</small><strong className={rateClass(evidence.point.metrics?.successRate ?? null)}>{rate(evidence.point.metrics?.successRate ?? null)}</strong></div>
              <div><b>{evidence.point.metrics?.success || 0} / {evidence.point.metrics?.total || 0}</b><small>{t.successes}</small><span>{evidence.point.metrics?.total ? `${t.failures} ${evidence.point.metrics.failed}` : t.noAttempts}</span></div>
            </div>
            <dl className="history-tooltip-speeds">
              <div><dt>{t.average}</dt><dd>{latency(evidence.point.metrics?.averageDurationMs)}</dd></div>
              <div><dt>{t.fastest}</dt><dd>{latency(evidence.point.metrics?.minimumDurationMs)}</dd></div>
              <div><dt>{t.slowest}</dt><dd>{latency(evidence.point.metrics?.maximumDurationMs)}</dd></div>
            </dl>
            <div className="history-tooltip-presence"><span>{t.availability} <b>{rate(evidence.point.availability)}</b></span><span>{t.observation} <b>{evidence.point.observedMinutes}/{evidence.point.expectedMinutes}m</b></span></div>
            <footer>{status(evidence.point.state)} · {t.timingNote}</footer>
          </div>,
          document.body,
        )}
      {selected && (
        <>
          <button
            className="agent-detail-scrim"
            aria-label={t.close}
            onClick={() => setSelectedId(null)}
          />
          <aside
            className="agent-detail"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agent-detail-title"
            onKeyDown={(event) => {
              if (event.key === "Escape") setSelectedId(null);
            }}
          >
            <header>
              <div>
                <small>{t.details}</small>
                <h3 id="agent-detail-title">{selected.label}</h3>
              </div>
              <button
                autoFocus
                className="icon-button"
                aria-label={t.close}
                title={t.close}
                onClick={() => setSelectedId(null)}
              >
                <X size={19} />
              </button>
            </header>
            <div className="agent-detail-body">
              {actionNotice && (
                <div className="alert alert-error" role="status">
                  {actionNotice}
                </div>
              )}
              {notice && (
                <div className="alert alert-error" role="status">
                  {notice}{" "}
                  {locale === "en"
                    ? "Showing the last successful snapshot."
                    : "当前展示上一次成功获取的记录。"}
                </div>
              )}
              <StatusBadge
                value={selected.state}
                label={selected.state === "draining" || selected.capabilities.paused ? `${locale === "en" ? "Paused" : "暂停接单"}（${selected.capabilities.pauseSource === "admin" || !selected.capabilities.paused ? (locale === "en" ? "Console" : "管理端发起") : (locale === "en" ? "Local Agent" : "本地发起")}）` : status(selected.state)}
              />
              <SchedulingNotice agent={selected} locale={locale} generatedAt={fleet?.generatedAt} />
              <dl className="agent-detail-stats">
                {[
                  [
                    ["online", "draining"].includes(selected.state)
                      ? t.current
                      : t.previous,
                    duration(
                      ["online", "draining"].includes(selected.state)
                        ? selected.presence.continuousOnlineSeconds
                        : selected.presence.lastConnectionSeconds,
                    ),
                  ],
                  [t.online24, duration(selected.presence.onlineSeconds24h)],
                  [t.availability, rate(selected.presence.availability24h)],
                  [t.coverage, rate(selected.presence.coveragePercent)],
                ].map(([name, value]) => (
                  <div key={name}>
                    <dt>{name}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <p className="muted-text">
                {t.sampling} {selected.presence.observedMinutes}/1440m
              </p>
              <h4>{t.machine}</h4>
              <dl className="agent-machine-grid">
                {[
                  [t.cpu, percent(selected.capabilities.system?.cpuPercent)],
                  [t.memory, `${percent(selected.capabilities.system?.memoryUsedPercent)} · ${bytes(selected.capabilities.system?.memoryFreeBytes)} free`],
                  [t.process, bytes(selected.capabilities.system?.processRssBytes)],
                  [t.load1, selected.capabilities.system?.load1 == null ? "—" : selected.capabilities.system.load1.toFixed(2)],
                  [t.uptime, duration(selected.capabilities.system?.uptimeSeconds ?? null)],
                  [t.sampled, timestamp(selected.capabilities.system?.sampledAt || null)],
                ].map(([name, value]) => (
                  <div key={name}>
                    <dt>{name}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <h4>{t.performance}</h4>
              <table className="agent-metric-table">
                <thead>
                  <tr>
                    <th>{t.window}</th>
                    <th>{locale === "en" ? "Success rate" : "成功率"}</th>
                    <th>{t.successes}</th>
                    <th>{t.duration}</th>
                    <th>{t.fastest}</th>
                    <th>{t.slowest}</th>
                  </tr>
                </thead>
                <tbody>
                  {["1m", "15m", "30m", "1h", "24h"].map((window) => {
                    const metric = selected.metrics[window];
                    return (
                      <tr key={window}>
                        <td>{window}</td>
                        <td>{rate(metric?.successRate)}</td>
                        <td>
                          {metric?.success || 0}/{metric?.total || 0}
                        </td>
                        <td>
                          {metric?.averageDurationMs == null
                            ? "—"
                            : `${(metric.averageDurationMs / 1000).toFixed(1)}s`}
                        </td>
                        <td>{latency(metric?.minimumDurationMs)}</td>
                        <td>{latency(metric?.maximumDurationMs)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <h4>{t.fullId}</h4>
              <div className="agent-detail-id">
                <code>{selected.deviceId || selected.id}</code>
                <button
                  className="icon-button"
                  aria-label={copied ? t.copied : t.copy}
                  title={copied ? t.copied : t.copy}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        selected.deviceId || selected.id,
                      );
                      setCopied(true);
                    } catch {
                      setActionNotice(
                        locale === "en"
                          ? "Unable to copy. Select and copy the device ID below."
                          : "复制失败，请选中下方设备 ID 手动复制。",
                      );
                    }
                  }}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
              <dl className="agent-detail-meta">
                {[
                  [t.platform, platform(selected.platform)],
                  [t.version, selected.version || "—"],
                  [locale === 'en' ? 'Desktop tray' : '桌面图标', ({ ready: locale === 'en' ? 'Registered' : '图标已注册', disabled: locale === 'en' ? 'Disabled' : '已关闭', 'waiting-for-desktop': locale === 'en' ? 'Waiting for desktop login' : '等待桌面登录', 'extension-required': locale === 'en' ? 'AppIndicator extension required' : '需启用 AppIndicator 扩展', error: locale === 'en' ? 'Startup failed' : '启动失败' } as Record<string, string>)[selected.capabilities.tray?.state || ''] || (locale === 'en' ? 'Waiting for status' : '等待上报')],
                  [
                    t.model,
                    `${selected.capabilities.model || "—"} / ${selected.capabilities.provider || "—"}`,
                  ],
                  [t.connected, timestamp(selected.presence.connectedAt)],
                  [t.lastSeen, timestamp(selected.lastSeenAt)],
                  [t.registered, timestamp(selected.createdAt)],
                  [t.approved, timestamp(selected.approvedAt)],
                  [t.disconnect, timestamp(selected.presence.lastDisconnectAt)],
                  [t.reason, reason(selected.presence.lastDisconnectReason)],
                ].map(([name, value]) => (
                  <div key={name}>
                    <dt>{name}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <AgentRemoteAccess key={selected.id} workerId={selected.id} online={selected.state === 'online' || selected.state === 'draining'} supported={selected.capabilities.sshManagement === true} restartSupported={selected.capabilities.remoteRestart === true} locale={locale} status={selected.capabilities.remoteAccess} platform={selected.platform} refresh={() => { void reload() }}/>
              <label className="agent-mode-setting">
                {t.mode}
                <select
                  aria-label={t.mode}
                  value={selected.mode}
                  disabled={pending || selected.state === "revoked"}
                  onChange={(event) =>
                    void mutate(`/api/admin/workers/${selected.id}`, "PATCH", {
                      mode: event.target.value,
                    })
                  }
                >
                  <option value="AUTO">
                    {locale === "en" ? "Automatic" : "自动"}
                  </option>
                  <option value="ASSISTED">
                    {locale === "en" ? "Assisted" : "辅助"}
                  </option>
                  <option value="MANUAL">
                    {locale === "en" ? "Manual" : "手动"}
                  </option>
                </select>
              </label>
              <label className="agent-mode-setting">
                {locale === "en" ? "Channels" : "通道数"}
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={selected.maxConcurrency}
                  disabled={pending || selected.state === "revoked"}
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    if (Number.isInteger(value) && value >= 1 && value <= 100)
                      void mutate(
                        `/api/admin/workers/${selected.id}`,
                        "PATCH",
                        { maxConcurrency: value },
                      );
                  }}
                />
                <small>
                  {locale === "en"
                    ? `Configured ${selected.maxConcurrency} · effective ${selected.effectiveConcurrency ?? selected.maxConcurrency}${selected.reportedConcurrency ? ` · Agent limit ${selected.reportedConcurrency}` : ""}`
                    : `配置 ${selected.maxConcurrency} · 当前有效 ${selected.effectiveConcurrency ?? selected.maxConcurrency}${selected.reportedConcurrency ? ` · Agent 上限 ${selected.reportedConcurrency}` : ""}`}
                </small>
              </label>
              <div className="agent-detail-actions">
                {["online", "draining"].includes(selected.state) && (
                  <button
                    className="button button-outline"
                    disabled={pending || !selected.capabilities.monitorRefresh}
                    title={!selected.capabilities.monitorRefresh ? (locale === "en" ? "Upgrade the Agent first" : "请先升级 Agent") : undefined}
                    onClick={() => void mutate(`/api/admin/agents/${selected.id}/refresh-monitor`, "POST", {}, locale === "en" ? "Monitor refresh delivered." : "已通知本地监控页刷新。")}
                  >
                    {locale === "en" ? "Refresh local monitor" : "刷新本地监控页"}
                  </button>
                )}
                {selected.state === "pending" && (
                  <button
                    className="button button-primary"
                    disabled={pending}
                    onClick={() => deviceAction(selected, "approve")}
                  >
                    <Check size={16} />
                    {t.approve}
                  </button>
                )}
                {["online", "draining"].includes(selected.state) && (
                  <button
                    className="button button-outline"
                    disabled={pending}
                    onClick={() =>
                      void mutate(
                        `/api/admin/workers/${selected.id}`,
                        "PATCH",
                        { diagnosticsEnabled: !selected.diagnosticsEnabled },
                      )
                    }
                  >
                    {selected.diagnosticsEnabled
                      ? t.diagnosticsOn
                      : t.diagnosticsOff}
                  </button>
                )}
                {["online", "draining"].includes(selected.state) && (
                  <button
                    className="button button-outline"
                    disabled={pending}
                    onClick={() =>
                      void mutate(
                        `/api/admin/workers/${selected.id}`,
                        "PATCH",
                        { draining: selected.state !== "draining" },
                      )
                    }
                  >
                    {selected.state === "draining" ? (
                      <Play size={16} />
                    ) : (
                      <Pause size={16} />
                    )}
                    {selected.state === "draining" ? t.resume : t.drain}
                  </button>
                )}
                <button
                  className="button button-outline text-danger"
                  disabled={
                    pending ||
                    selected.activeTasks > 0 ||
                    !selected.enrollmentId
                  }
                  onClick={() => deviceAction(selected, "delete")}
                >
                  <Trash2 size={16} />
                  {t.remove}
                </button>
              </div>
            </div>
          </aside>
        </>
      )}
    </section>
  );
}
